import React, { useEffect, useState } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import './Classroom.css';
import { useTranslation } from 'react-i18next';
import { Button, Typography, Spin, Table, message } from 'antd';

const { Text, Title } = Typography;

function Classroom() {
  const { t, i18n } = useTranslation();
  const [classrooms, setClassrooms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('this');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const API_URL = process.env.REACT_APP_API_URL;

  // Define standard time slots
  const timeSlots = [
    "10.00 - 12.00",
    "13.00 - 15.00",
    "15.00 - 17.00",
    "17.00 - 19.00"
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const [classroomResponse, classResponse] = await Promise.all([
          axios.get(`${API_URL}/classrooms`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/classes`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setClassrooms(classroomResponse.data);
        setClasses(classResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        message.error(t('classroom.fetch_error'));
        setError(t('classroom.fetch_error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL, t]);

  const formatDate = (date) => {
    const options = { day: '2-digit', month: 'long', year: 'numeric' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date);
  };

  const getClassEventsForWeek = (centerDate) => {
    // Reset time components to midnight
    const startDate = new Date(centerDate);
    startDate.setHours(0, 0, 0, 0);
    startDate.setDate(centerDate.getDate() - centerDate.getDay());
    
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    endDate.setHours(23, 59, 59, 999);  // End of day
  
    return classes.filter(cls => {
      // Create date with consistent timezone handling
      const clsDate = new Date(cls.date);
      clsDate.setHours(0, 0, 0, 0);  // Reset time component
      
      return clsDate >= startDate && clsDate <= endDate;
    });
  };

  // Find the closest time slot for a given class time
  const findClosestTimeSlot = (scheduleTime) => {
    // Convert time to comparable format (e.g., "10:30" to minutes: 10*60 + 30 = 630)
    const [hours, minutes] = scheduleTime.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;
    
    // Map time slots to their middle point in minutes
    const timeSlotMiddlePoints = timeSlots.map(slot => {
      const [startTime, endTime] = slot.split(' - ');
      const [startHours, startMinutes] = startTime.split('.').map(Number);
      const [endHours, endMinutes] = endTime.split('.').map(Number);
      const startInMinutes = startHours * 60 + startMinutes;
      const endInMinutes = endHours * 60 + endMinutes;
      return {
        slot,
        middle: (startInMinutes + endInMinutes) / 2
      };
    });
    
    // Find the closest time slot
    let closestSlot = timeSlots[0];
    let minDifference = Infinity;
    
    timeSlotMiddlePoints.forEach(({ slot, middle }) => {
      const difference = Math.abs(timeInMinutes - middle);
      if (difference < minDifference) {
        minDifference = difference;
        closestSlot = slot;
      }
    });
    
    return closestSlot;
  };

  // Sort classrooms by number
  const sortedClassrooms = [...classrooms].sort((a, b) => {
    const numA = a.number.match(/\d+/g);
    const numB = b.number.match(/\d+/g);
    return numA && numB ? parseInt(numA[0], 10) - parseInt(numB[0], 10) : a.number.localeCompare(b.number);
  });

  const renderDayScheduleTable = (date) => {
    const dateCopy = new Date(date);
    const classEvents = getClassEventsForWeek(selectedDate);
    const dayEvents = classEvents.filter(cls => {
      const eventDate = new Date(cls.date);
      return eventDate.toDateString() === dateCopy.toDateString();
    });

    // Create table columns based on classrooms only (no time slot column)
    const columns = sortedClassrooms.map(classroom => ({
      title: `${t('classroom.room')} ${classroom.number}`,
      dataIndex: classroom.number,
      key: classroom.number,
      render: (text) => {
        if (!text) return null;
        return (
          <div className="class-cell">
            <div><Text strong>{text.subject_name}</Text></div>
            <div><Text type="secondary">{text.schedule_time}-{text.end_time}</Text></div>
            <div><Text type="secondary">{text.nickname}</Text></div>
          </div>
        );
      },
    }));

    // Map each class to its closest time slot
    const classesByTimeSlot = {};
    
    // Initialize empty time slots
    timeSlots.forEach(slot => {
      classesByTimeSlot[slot] = {};
    });
    
    // Assign classes to their closest time slot
    dayEvents.forEach(event => {
      const closestSlot = findClosestTimeSlot(event.schedule_time);
      
      // If this classroom doesn't have a class in this time slot yet
      if (!classesByTimeSlot[closestSlot][event.classroom_number]) {
        classesByTimeSlot[closestSlot][event.classroom_number] = {
          subject_name: event.subject_name,
          nickname: event.nickname,
          schedule_time: event.schedule_time.slice(0, 5),
          end_time: event.end_time.slice(0, 5)
        };
      } else {
        // If there's already a class for this classroom in this slot,
        // we could handle conflicts here if needed
        console.log(`Scheduling conflict in ${event.classroom_number} at ${closestSlot}`);
      }
    });
    
    // Create data source for the table
    const dataSource = timeSlots.map(timeSlot => {
      const rowData = { 
        key: timeSlot
      };
      
      // Add class data for each classroom at this time slot
      sortedClassrooms.forEach(classroom => {
        if (classesByTimeSlot[timeSlot][classroom.number]) {
          rowData[classroom.number] = classesByTimeSlot[timeSlot][classroom.number];
        }
      });
      
      return rowData;
    });

    return (
      <div className="day-schedule-table">
        <Table 
          dataSource={dataSource} 
          columns={columns} 
          pagination={false}
          bordered
          size="middle"
          className="schedule-table"
        />
      </div>
    );
  };

  const renderWeekSchedule = () => {
    const weekDates = [];
    const startDate = new Date(selectedDate);
    startDate.setDate(selectedDate.getDate() - selectedDate.getDay());
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      weekDates.push(date);
    }

    return (
      <div className="week-schedule">
        {weekDates.map((date, index) => (
          <div key={index} className="day-schedule">
            <div className="day-header">
              <Title level={4}>
                {t(`days.${date.toLocaleDateString(i18n.language, { weekday: 'long' }).toLowerCase()}`)} - {formatDate(date)}
              </Title>
            </div>
            {renderDayScheduleTable(date)}
          </div>
        ))}
      </div>
    );
  };

  const handlePreviousWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
    setActiveTab('previous');
  };
  
  const handleNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
    setActiveTab('next');
  };

  const handleThisWeek = () => {
    setSelectedDate(new Date());
    setActiveTab('this');
  };

  if (loading) return <Spin tip={t('loading')} />;
  if (error) return <Text type="danger">{error}</Text>;

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="classroom-page-container">
        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '24px' }}>
          <Button
            onClick={handlePreviousWeek}
            type={activeTab === 'previous' ? 'primary' : 'default'}
            style={{ padding: '8px 24px', fontSize: '16px' }}
          >
            {t('navigation.previous_week')}
          </Button>
          <Button
            onClick={handleThisWeek}
            type={activeTab === 'this' ? 'primary' : 'default'}
            style={{ padding: '8px 24px', fontSize: '16px' }}
          >
            {t('navigation.this_week')}
          </Button>
          <Button
            onClick={handleNextWeek}
            type={activeTab === 'next' ? 'primary' : 'default'}
            style={{ padding: '8px 24px', fontSize: '16px' }}
          >
            {t('navigation.next_week')}
          </Button>
        </div>

        {renderWeekSchedule()}
      </div>
    </div>
  );
}

export default withRoleAccess(Classroom, ['admin', 'superadmin', 'teacher', 'student']);